import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, ProgressBar, Row, Col, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCompanyDepartmentOptions } from '../hooks/useCompanyDepartmentOptions';

const ProfileCompletion = ({ onComplete }) => {
 const { user, updateUser } = useAuth();
 const { companies, departments, loading: optionsLoading } = useCompanyDepartmentOptions();
 const navigate = useNavigate();
 const location = useLocation();
 const [loading, setLoading] = useState(false);
 const [profileData, setProfileData] = useState({
 fullName: '',
 phone: '',
 department: '',
 company: '',
 avatar: ''
 });
 const [completionStatus, setCompletionStatus] = useState(null);
 const [fieldErrors, setFieldErrors] = useState({});
 const [validatedFields, setValidatedFields] = useState({});
 const [isNavigating, setIsNavigating] = useState(false);
 const [hasNavigated, setHasNavigated] = useState(false);

 useEffect(() => {
 fetchProfileData();
 }, []);

 // Navigation is now handled directly in handleSubmit when profile becomes complete
 // No need for useEffect auto-navigation

 const fetchProfileData = async () => {
 try {
 setLoading(true);
 const response = await API.get('/profile/profile');
 const userData = response.data.user;
 
 setProfileData({
 fullName: userData.fullName || '',
 phone: userData.phone || '',
 department: userData.department || '',
 company: userData.company || '',
 avatar: userData.avatar || ''
 });
 
 setCompletionStatus(userData.profileCompletion);
 } catch (error) {
 console.error('Error fetching profile:', error);
 toast.error('Không thể tải thông tin profile');
 } finally {
 setLoading(false);
 }
 };

 const validateField = async (field, value) => {
 try {
 const response = await API.post('/profile/validate-field', { field, value });
 const isValid = response.data.isValid;
 
 setValidatedFields(prev => ({
 ...prev,
 [field]: isValid
 }));
 
 if (!isValid) {
 setFieldErrors(prev => ({
 ...prev,
 [field]: getFieldErrorMessage(field)
 }));
 } else {
 setFieldErrors(prev => {
 const newErrors = { ...prev };
 delete newErrors[field];
 return newErrors;
 });
 }
 
 return isValid;
 } catch (error) {
 console.error('Field validation error:', error);
 return false;
 }
 };

 const getFieldErrorMessage = (field) => {
 const messages = {
 fullName: 'Họ tên phải có ít nhất 2 ký tự',
 phone: 'Số điện thoại phải có 10-15 chữ số',
 department: 'Vui lòng chọn phòng ban',
 company: 'Vui lòng chọn công ty',
 avatar: 'URL ảnh không hợp lệ'
 };
 return messages[field] || 'Trường này không hợp lệ';
 };

 const handleInputChange = async (field, value) => {
 setProfileData(prev => ({
 ...prev,
 [field]: value
 }));

 // Real-time validation
 if (value.trim()) {
 await validateField(field, value);
 } else {
 setValidatedFields(prev => {
 const newValidated = { ...prev };
 delete newValidated[field];
 return newValidated;
 });

 // Avatar is optional, so don't show error when empty
 if (field !== 'avatar') {
 setFieldErrors(prev => ({
 ...prev,
 [field]: getFieldErrorMessage(field)
 }));
 } else {
 setFieldErrors(prev => {
 const newErrors = { ...prev };
 delete newErrors[field];
 return newErrors;
 });
 }
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);

 try {
 // Validate all required fields (avatar is optional)
 const requiredFields = ['fullName', 'phone', 'department', 'company'];
 const validationPromises = requiredFields.map(field =>
 validateField(field, profileData[field])
 );

 // Validate avatar only if it has a value
 if (profileData.avatar && profileData.avatar.trim()) {
 validationPromises.push(validateField('avatar', profileData.avatar));
 }

 const validationResults = await Promise.all(validationPromises);
 const allValid = validationResults.every(result => result);

 if (!allValid) {
 toast.error('Vui lòng điền đầy đủ và chính xác tất cả thông tin bắt buộc');
 setLoading(false);
 return;
 }

 // Update profile
 const response = await API.put('/profile/update-profile', profileData);

 if (response.data.success) {
 const updatedUser = response.data.user;
 const newCompletionStatus = updatedUser.profileCompletion;

 console.log('Profile updated successfully:', newCompletionStatus);

 setCompletionStatus(newCompletionStatus);

 // Update user in auth context
 updateUser(updatedUser);

 // Show appropriate message based on completion status
 if (newCompletionStatus.isComplete) {
 toast.success('Thông tin cá nhân đã hoàn thành! Đang chuyển đến trang chủ...', {
 autoClose: 2000
 });
 console.log('Profile is now complete, navigating to home...');

 // Set navigating state
 setIsNavigating(true);
 setHasNavigated(true);

 // Call onComplete callback if provided
 if (onComplete) {
 onComplete(updatedUser);
 }

 // Navigate directly here instead of useEffect
 setTimeout(() => {
 const intendedPath = location.state?.from || '/';
 console.log('Navigating to:', intendedPath);
 navigate(intendedPath, { replace: true });
 }, 2000);
 } else {
 toast.success(`Cập nhật thành công! Tiến độ: ${newCompletionStatus.completionPercentage}%`);
 }
 }
 } catch (error) {
 console.error('Profile update error:', error);
 if (error.response?.data?.errors) {
 error.response.data.errors.forEach(err => toast.error(err));
 } else {
 toast.error('Lỗi khi cập nhật profile');
 }
 } finally {
 setLoading(false);
 }
 };

 const getFieldIcon = (field) => {
 if (validatedFields[field]) return '';
 if (fieldErrors[field]) return '';
 return '';
 };

 const getProgressVariant = (percentage) => {
 if (percentage < 40) return 'danger';
 if (percentage < 80) return 'warning';
 return 'success';
 };

 if ((loading && !completionStatus) || optionsLoading) {
 return (
 <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
 <Spinner animation="border" role="status">
 <span className="visually-hidden">Đang tải...</span>
 </Spinner>
 </div>
 );
 }

 return (
 <div className="container mt-4">
 <Row className="justify-content-center">
 <Col md={8} lg={6}>
 <Card>
 <Card.Header className="bg-primary text-white">
 <h4 className="mb-0">
 <i className="fas fa-user-edit me-2"></i>
 Hoàn thành thông tin cá nhân
 </h4>
 </Card.Header>
 <Card.Body>
 {completionStatus && (
 <div className="mb-4">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <span><strong>Tiến độ hoàn thành:</strong></span>
 <span className="badge bg-primary">{completionStatus.completionPercentage}%</span>
 </div>
 <ProgressBar 
 now={completionStatus.completionPercentage} 
 variant={getProgressVariant(completionStatus.completionPercentage)}
 className="mb-2"
 />
 <small className="text-muted">
 {completionStatus.completedFields.length} / {completionStatus.requiredFields.length} trường bắt buộc đã hoàn thành
 </small>
 </div>
 )}

 {completionStatus?.isComplete ? (
 <Alert variant="success">
 <Alert.Heading>
 <i className="fas fa-check-circle me-2"></i>
 Thông tin cá nhân đã hoàn thành!
 </Alert.Heading>
 <p>Tuyệt vời! Bạn đã hoàn thành tất cả thông tin bắt buộc.</p>
 {isNavigating && (
 <div className="d-flex align-items-center">
 <Spinner animation="border" size="sm" className="me-2" />
 <span>Đang chuyển đến trang chủ...</span>
 </div>
 )}
 </Alert>
 ) : (
 <Alert variant="warning">
 <Alert.Heading>Thông tin cá nhân chưa đầy đủ</Alert.Heading>
 <p>Vui lòng hoàn thành tất cả thông tin bắt buộc để có thể sử dụng hệ thống.</p>
 {completionStatus?.missingFields && (
 <div>
 <strong>Các trường còn thiếu:</strong>
 <ul className="mb-0 mt-1">
 {completionStatus.missingFields.map(field => (
 <li key={field}>{getFieldDisplayName(field)}</li>
 ))}
 </ul>
 </div>
 )}
 </Alert>
 )}

 <Form onSubmit={handleSubmit}>
 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>
 {getFieldIcon('fullName')} Họ và tên <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="text"
 value={profileData.fullName}
 onChange={(e) => handleInputChange('fullName', e.target.value)}
 isInvalid={!!fieldErrors.fullName}
 isValid={validatedFields.fullName}
 placeholder="Nhập họ và tên đầy đủ"
 />
 <Form.Control.Feedback type="invalid">
 {fieldErrors.fullName}
 </Form.Control.Feedback>
 </Form.Group>
 </Col>
 
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>
 {getFieldIcon('phone')} Số điện thoại <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="tel"
 value={profileData.phone}
 onChange={(e) => handleInputChange('phone', e.target.value)}
 isInvalid={!!fieldErrors.phone}
 isValid={validatedFields.phone}
 placeholder="Nhập số điện thoại"
 />
 <Form.Control.Feedback type="invalid">
 {fieldErrors.phone}
 </Form.Control.Feedback>
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>
 {getFieldIcon('department')} Phòng ban <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 value={profileData.department}
 onChange={(e) => handleInputChange('department', e.target.value)}
 isInvalid={!!fieldErrors.department}
 isValid={validatedFields.department}
 >
 <option value="">Chọn phòng ban</option>
 {departments.map((dept, index) => (
 <option key={index} value={dept}>
 {dept}
 </option>
 ))}
 </Form.Select>
 <Form.Control.Feedback type="invalid">
 {fieldErrors.department}
 </Form.Control.Feedback>
 </Form.Group>
 </Col>
 
 <Col md={6}>
 <Form.Group className="mb-3">
 <Form.Label>
 {getFieldIcon('company')} Công ty <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 value={profileData.company}
 onChange={(e) => handleInputChange('company', e.target.value)}
 isInvalid={!!fieldErrors.company}
 isValid={validatedFields.company}
 >
 <option value="">Chọn công ty</option>
 {companies.map((company, index) => (
 <option key={index} value={company}>
 {company}
 </option>
 ))}
 </Form.Select>
 <Form.Control.Feedback type="invalid">
 {fieldErrors.company}
 </Form.Control.Feedback>
 </Form.Group>
 </Col>
 </Row>

 <Form.Group className="mb-4">
 <Form.Label>
 {getFieldIcon('avatar')} Ảnh đại diện <span className="text-muted">(Không bắt buộc)</span>
 </Form.Label>
 <Form.Control
 type="url"
 value={profileData.avatar}
 onChange={(e) => handleInputChange('avatar', e.target.value)}
 isInvalid={!!fieldErrors.avatar}
 isValid={validatedFields.avatar}
 placeholder="Nhập URL ảnh đại diện"
 />
 <Form.Control.Feedback type="invalid">
 {fieldErrors.avatar}
 </Form.Control.Feedback>
 {profileData.avatar && (
 <div className="mt-2">
 <img 
 src={profileData.avatar} 
 alt="Preview" 
 style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '50%' }}
 onError={(e) => {
 e.target.style.display = 'none';
 setFieldErrors(prev => ({
 ...prev,
 avatar: 'URL ảnh không hợp lệ'
 }));
 }}
 />
 </div>
 )}
 </Form.Group>

 <div className="d-grid">
 <Button
 type="submit"
 variant="primary"
 size="lg"
 disabled={loading || Object.keys(fieldErrors).filter(key => key !== 'avatar').length > 0 || isNavigating}
 >
 {loading ? (
 <>
 <Spinner animation="border" size="sm" className="me-2" />
 Đang cập nhật...
 </>
 ) : isNavigating ? (
 <>
 <Spinner animation="border" size="sm" className="me-2" />
 Đang chuyển trang...
 </>
 ) : (
 <>
 <i className="fas fa-save me-2"></i>
 {completionStatus?.isComplete ? 'Cập nhật thông tin' : 'Hoàn thành profile'}
 </>
 )}
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </div>
 );
};

const getFieldDisplayName = (field) => {
 const names = {
 fullName: 'Họ và tên',
 phone: 'Số điện thoại',
 department: 'Phòng ban',
 company: 'Công ty',
 avatar: 'Ảnh đại diện'
 };
 return names[field] || field;
};

export default ProfileCompletion;
